import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { showApiError, showSuccess } from "@/lib/toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addFirmLicenceToFirm, getLinkedLicences } from "@/sideEffects/api";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { LoadingSmall } from "@/components/LoadingSmall";
import { isAfter, parse } from "date-fns";

interface Props {
  firmId: number;
  firmGuid: string;
}

export const AddNewLicenceDialog = ({ firmId, firmGuid }: Props) => {
  const { t } = useTranslation();
  const [open, onOpenChange] = useState(false);

  const { data: linkedLicences } = useQuery({
    queryKey: ["getLinkedLicences"],
    queryFn: () =>
      getLinkedLicences(
        { pageNumber: 1, numberOfObjectsPerPage: 999999 },
        {
          FirmGuid: firmGuid,
        }
      ),
  });

  const linkedLicencesData = linkedLicences?.data?.result;
  const hasExpiredLicence = () => {
    if (!linkedLicencesData?.length) return true;
    return linkedLicencesData?.some((licence) => {
      try {
        const licenceExpiryDate = parse(licence.licenceExpiry, "dd MMMM yyyy", new Date());
        return !isAfter(licenceExpiryDate, new Date()); // Return true if licence has expired
      } catch (error) {
        console.error("Error parsing date:", licence.licenceExpiry, error);
        return false; // Treat unparseable dates as not expired
      }
    });
  };

  const formSchema = z.object({
    totalLicence: z.string().min(1),
    expiryDate: hasExpiredLicence() ? z.string().min(1) : z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      totalLicence: "",
      expiryDate: "",
    },
  });

  const queryClient = useQueryClient();
  const addLicenceMutation = useMutation({
    mutationFn: (values: z.infer<typeof formSchema>) =>
      addFirmLicenceToFirm({
        totalLicence: Number(values.totalLicence),
        expiryDate: values.expiryDate || "",
        firmId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getLinkedLicences"],
      });
      showSuccess(t("common.addedSuccess"));
      onOpenChange(false);
    },
    onError: showApiError,
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    addLicenceMutation.mutate(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>{t("common.addNewEntity", { entity: t("firm.licences") })}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.addNewEntity", { entity: t("firm.licences") })}</DialogTitle>
          <DialogDescription>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                  control={form.control}
                  name="totalLicence"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("firm.totalLicence")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("firm.totalLicence")} type="number" min="0" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {hasExpiredLicence() && (
                  <FormField
                    control={form.control}
                    name="expiryDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("firm.expiryDate")}</FormLabel>
                        <FormControl>
                          <div>
                            <DatePicker value={field.value} onChange={field.onChange} />
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}
                <Button type="submit" disabled={addLicenceMutation.isPending}>
                  {t("common.add")}
                  {addLicenceMutation.isPending && <LoadingSmall />}
                </Button>
              </form>
            </Form>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
